import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import CustomLink from './link';
import TickerTape from './tickertape';
import Newsletter from './newsletter-signup';
import Swirl from '../svg/swirl';

const Footer = () => {
	const SITE_KEY = process.env.GATSBY_SITE_KEY || 'uk';
	const LOCALE = (process.env.GATSBY_SITE_LOCALE || '').toLowerCase();
	const IS_DE = SITE_KEY === 'de' || LOCALE.startsWith('de');

	const { footerEn, footerDe, datoCmsSite } = useStaticQuery(graphql`
		{
			footerEn: datoCmsFooter(locale: "en") {
				newsletterButtonText
				newsletterPlaceholderText
				newsletterSignupText
				smallPrintText
				tickerTapeFormat
				tickerTape {
					text
					emoji {
						spin
						image {
							alt
							url(imgixParams: { w: "64" })
						}
					}
				}
				links {
					id: originalId
					url
					text
				}
				smallLeftLinks {
					url
					text
				}
				smallRightLinks {
					url
					text
				}
				bcorp {
					alt
					url(
						imgixParams: {
							auto: "compress"
							w: "800"
							trim: "auto"
						}
					)
				}
			}

			footerDe: datoCmsFooter(locale: "de") {
				newsletterButtonText
				newsletterPlaceholderText
				newsletterSignupText
				smallPrintText
				tickerTapeFormat
				tickerTape {
					text
					emoji {
						spin
						image {
							alt
							url(imgixParams: { w: "64" })
						}
					}
				}
				links {
					id: originalId
					url
					text
				}
				smallLeftLinks {
					url
					text
				}
				smallRightLinks {
					url
					text
				}
				bcorp {
					alt
					url(
						imgixParams: {
							auto: "compress"
							w: "800"
							trim: "auto"
						}
					)
				}
			}

			datoCmsSite {
				name
			}
		}
	`);

	// Prefer DE when in DE mode, but gracefully fall back if the DE record is missing
	const data = (IS_DE ? footerDe : footerEn) || footerEn;

	const getCurrentYear = () => new Date().getFullYear();

	// If neither locale exists, fail safely (avoids crashing build if Dato is empty)
	if (!data) return null;

	return (
		<footer className='page-footer background-dark-green'>
			<TickerTape data={data.tickerTape} format={data.tickerTapeFormat} />

			{!IS_DE && (
				<div className='footer-content'>
					<p className='lede'>{data.newsletterSignupText}</p>
					<Newsletter
						button={data.newsletterButtonText}
						placeholder={data.newsletterPlaceholderText}
						smallprint={data.smallPrintText}
					/>
				</div>
			)}

			<div className='footer-links'>
				{data.links?.map((link) => (
					<CustomLink
						key={link.id || link.url}
						url={link.url}
						classes='btn btn--skeleton'
					>
						<span className='btn-text'>{link.text}</span>
					</CustomLink>
				))}
			</div>

			<div className='footer-bottom'>
				<Swirl />

				<div className='link-items link-items-left'>
					{data.smallLeftLinks?.map((link) => (
						<CustomLink
							key={link.url}
							url={link.url}
							classes='footer-small-link'
						>
							{link.text}
						</CustomLink>
					))}
				</div>

				<div className='copyright-bcorp'>
					{data.bcorp?.url && (
						<img
							className='bcorp-logo'
							src={data.bcorp.url}
							alt={data.bcorp.alt}
							loading='lazy'
						/>
					)}
					<p className='copyright'>
						<span>
							© {getCurrentYear()} {datoCmsSite?.name}
						</span>
					</p>
				</div>

				<div className='link-items link-items-right'>
					{data.smallRightLinks?.map((link) => (
						<CustomLink
							key={link.url}
							url={link.url}
							classes='footer-small-link'
						>
							{link.text}
						</CustomLink>
					))}
				</div>
			</div>

			<div className='footer-credit'>
				<p>
					Site designed and built by{' '}
					<a
						href='https://georgekerridge.com'
						rel='noreferrer'
						target='_blank'
					>
						George Kerridge
					</a>
				</p>
			</div>
		</footer>
	);
};

export default Footer;
